import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Plus, DollarSign, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useFavoritesContext } from '../contexts/FavoritesContext';
import { useAuth } from '../hooks/useAuth';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitTool: () => void;
  onDonate: () => void;
}
export function MobileMenu({
  isOpen,
  onClose,
  onSubmitTool,
  onDonate
}: MobileMenuProps) {
  const {
    favorites
  } = useFavoritesContext();
  const {
    user,
    signOut
  } = useAuth();
  const handleSignOut = () => {
    signOut();
    onClose();
  };
  return <AnimatePresence data-id="element-362">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" data-id="element-363" />

          {/* Menu Panel */}
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300
      }} className="fixed top-0 right-0 h-full w-80 bg-gradient-to-b from-black via-gray-950 to-black z-50 flex flex-col" data-id="element-364">
            {/* Close Button */}
            <div className="flex justify-end p-6" data-id="element-365">
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/5 transition-colors" data-id="element-366">
                <X size={24} className="text-white/60" data-id="element-367" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto px-8 pb-8" data-id="element-368">
              <nav className="space-y-6" data-id="element-369">
                {/* Favorites */}
                <Link to="/favorites" onClick={onClose} className="flex items-center justify-between px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 group" data-id="element-370">
                  <div className="flex items-center gap-4" data-id="element-371">
                    <Heart size={22} className="text-pink-400/60 group-hover:text-pink-400 transition-colors" data-id="element-372" />
                    <span className="text-white text-lg font-light" data-id="element-373">
                      Favorites
                    </span>
                  </div>
                  {favorites.length > 0 && <span className="px-3 py-1 bg-gradient-to-r from-pink-500/20 to-pink-600/20 text-pink-300 text-sm font-medium rounded-full" data-id="element-374">
                      {favorites.length}
                    </span>}
                </Link>

                {/* Subscription Checker */}
                <Link to="/subscriptions" onClick={onClose} className="flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 group" data-id="element-375">
                  <DollarSign size={22} className="text-purple-400/60 group-hover:text-purple-400 transition-colors" data-id="element-376" />
                  <span className="text-white text-lg font-light" data-id="element-377">
                    Check Subscriptions
                  </span>
                </Link>

                {/* Submit Tool */}
                <button onClick={() => {
              onSubmitTool();
              onClose();
            }} className="w-full flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 group" data-id="element-378">
                  <Plus size={22} className="text-cyan-400/60 group-hover:text-cyan-400 transition-colors" data-id="element-379" />
                  <span className="text-white text-lg font-light" data-id="element-380">
                    Submit Tool
                  </span>
                </button>

                {/* Donate */}
                <button onClick={() => {
              onDonate();
              onClose();
            }} className="w-full flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 group" data-id="element-381">
                  <DollarSign size={22} className="text-green-400/60 group-hover:text-green-400 transition-colors" data-id="element-382" />
                  <span className="text-white text-lg font-light" data-id="element-383">Donate</span>
                </button>
              </nav>
            </div>

            {/* User Profile (if logged in) - Moved to bottom */}
            {user && <div className="px-8 pb-4" data-id="element-384">
                <div className="p-4 rounded-xl bg-white/5 border border-white/10" data-id="element-385">
                  <div className="flex items-center gap-3 mb-3" data-id="element-386">
                    <img src={user.user_metadata?.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`} alt="Profile" className="w-12 h-12 rounded-full ring-2 ring-white/10" data-id="element-387" />
                    <div className="flex-1 min-w-0" data-id="element-388">
                      <p className="text-white font-medium text-base truncate" data-id="element-389">
                        {user.email?.split('@')[0]}
                      </p>
                      <p className="text-gray-500 text-xs truncate" data-id="element-390">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <button onClick={handleSignOut} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-gradient-to-r hover:from-cyan-500/10 hover:to-purple-500/10 transition-all duration-300 text-white/80 hover:text-white group" data-id="element-391">
                    <LogOut size={16} className="text-white/40 group-hover:text-white/60 transition-colors" data-id="element-392" />
                    <span className="text-sm" data-id="element-393">Sign out</span>
                  </button>
                </div>
              </div>}

            {/* Footer with X Logo */}
            <div className="p-8 border-t border-white/5" data-id="element-394">
              <div className="flex items-center justify-between" data-id="element-395">
                <p className="text-xs text-gray-600" data-id="element-396">FAIND © 2024</p>
                <a href="https://x.com/faindyourapp" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-white/5 transition-colors group" data-id="element-397">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-600 group-hover:text-white transition-colors" fill="currentColor" data-id="element-398">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" data-id="element-399" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}